import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Tooltip,
  Select,
  MenuItem,
  TextField,
  Button,
  Checkbox
} from '@mui/material';
import { fabric } from 'fabric';
import { ArrowBack, Clear } from '@mui/icons-material';
import { PopoverListButtons } from '../../../Utils/styledComps';
import { getPoints } from '../../Tools/paintTools';
import { Bezier } from 'bezier-js';
import CustomRotateCopiesPopoverContent from './RotationCopies';
const CustomPathPopoverContent = ({
  selectedFeature,
  path,
  handleBackButtonClick,
  handlePopoverClose,
  mainCanvas,
  values,
  setValues,
  handlePathRotateCopies,
  rotationSettings,
  updateRotationSettings,         
  applyColorToSelectedCopy,
  setSelectedColorForRotationCopy,
  selectedColorForRotationCopy,
  selectedStrokeForRotationCopy,
setSelectedStrokeForRotationCopy,
  color,
  handleSpraying,
  opacityObj,
  changeOpacityOfObj
}) => {
  const handleChange = (e) =>{
    const {name, value} = e.target;
    if(name.toLowerCase().includes('rotate')){
      setValues((prev)=>({
        ...prev,
        [name]: e.target?.checked
      }))
    }else if(name.toLowerCase().includes("mode")){
    setValues((prev)=>({
      ...prev,
      [name]:value
    }))
  }else if(name === 'tangentialOff'){
    setValues((prev)=>({
      ...prev,
      [name]:value > 100 ? 100 : value < 0 ? 0 : parseFloat(value)
    }))
  }
  else{
    setValues((prev)=>({
      ...prev,
      [name]:parseFloat(value)
    }))
  }
    console.log('handleChange: ', name, e.target, values);
  }

  const [copiedObject, setCopiedObject] = useState(null);
  
  function handleCopyShape() {
  const canvas = mainCanvas;

  if (!canvas) {
    console.error("Canvas not ready");
    return;
  }

  const activeObject = canvas.getActiveObject();

  console.log("Copy clicked. Active object:", activeObject);

  if (!activeObject) {
    alert("Select an object first");
    return;
  }

  activeObject.clone(clone => {
    console.log("Clone created:", clone);

    clone.set({
      originX: "center",
      originY: "center"
    });

    clone.setCoords();

    canvas.patternObject = clone;
    setCopiedObject(clone); // Trigger React re-render

    console.log("✔ Pattern stored on canvas.patternObject", canvas.patternObject);
  });
}

  function makeClonesActive(mainCanvas) {
    // Collect all objects with the "distributeCopy" class
    const clones = [];
    mainCanvas.forEachObject((obj) => {
        if (obj.class?.includes("distributeCopy")) {
            clones.push(obj);
        }
    });

    if (clones.length === 0) {
        console.warn("No distributeCopy objects found to make active.");
        return;
    }

    // Create a fabric.ActiveSelection from the clones
    const activeSelection = new fabric.ActiveSelection(clones, {
        canvas: mainCanvas, // Associate it with the canvas
    });

    // Set it as the canvas's active object
    mainCanvas.setActiveObject(activeSelection);

    // Render changes
    mainCanvas.requestRenderAll();
}

function distributeAlongPath() {
  const canvas = mainCanvas;

  /* ---------------------------------------------------
   * 1️⃣ Validate copied pattern
   * --------------------------------------------------- */
  const patternObject = canvas.patternObject;

  if (!patternObject) {
    alert("Please copy a pattern object first");
    return;
  }

  /* ---------------------------------------------------
   * 2️⃣ Collect path objects (single OR multi-select)
   * --------------------------------------------------- */
  const activeObject = canvas.getActiveObject();
  let pathObjects = [];

  if (!activeObject) {
    alert("Please select at least one path object");
    return;
  }

  if (activeObject.type === "activeSelection") {
    activeObject.forEachObject(obj => {
      if (obj.path) {
        pathObjects.push(obj);
      }
    });
  } else if (activeObject.path) {
    pathObjects.push(activeObject);
  }

  if (pathObjects.length === 0) {
    alert("Please select at least one path object");
    return;
  }

  /* ---------------------------------------------------
   * 3️⃣ Remove old distributed clones
   * --------------------------------------------------- */
  canvas.getObjects().forEach(obj => {
    if (obj.class?.startsWith("distributeCopy")) {
      canvas.remove(obj);
    }
  });

  /* ---------------------------------------------------
   * 4️⃣ Calculate pattern size (ABSOLUTE, STABLE)
   * --------------------------------------------------- */
  const sizeClone = fabric.util.object.clone(patternObject);
  sizeClone.set({
    left: 0,
    top: 0,
    originX: "center",
    originY: "center",
    angle: 0,
    scaleX: patternObject.scaleX,
    scaleY: patternObject.scaleY
  });
  sizeClone.setCoords();

  const bbox = sizeClone.getBoundingRect(true, true);
  const patternSize = values.rotate ? bbox.height : bbox.width;

  /* ---------------------------------------------------
   * 5️⃣ Distribution for EACH path object
   * --------------------------------------------------- */
  pathObjects.forEach(pathObject => {
    const path = pathObject.path;
    const transformMatrix = pathObject.calcTransformMatrix();

    const pathLength = calculatePathLength(
      path,
      transformMatrix,
      pathObject
    );

    if (pathLength <= 0) return;

    const spacing = values.spacing || 0;
    const normalOffset = values.normalOff || 0;
    const tangentialOffset = values.tangentialOff || 0;

    const count = Math.max(
      1,
      Math.floor(pathLength / (patternSize + spacing))
    );

    const step = pathLength / count;

    /* ---------------------------------------------------
     * 6️⃣ Main distribution loop
     * --------------------------------------------------- */
    for (let i = 0; i < count; i++) {
      const distance = tangentialOffset + i * step;

      const point = getPointAtLength(
        path,
        distance,
        pathObject,
        transformMatrix
      );

      /* ---------- Clone pattern ---------- */
      const clone = fabric.util.object.clone(patternObject);

      clone.set({
        originX: "center",
        originY: "center",
        left: point.x,
        top: point.y
      });

      /* ---------- Rotation ---------- */
      let angle = 0;
      if (values.rotate) {
        angle = 90;
      } else if (values.tangentialRotate) {
        angle = getTangentAngle(path, distance, pathObject);
      }

      clone.angle = angle;

      /* ---------- Normal offset ---------- */
      if (normalOffset !== 0 && values.tangentialRotate) {
        const rad = (angle * Math.PI) / 180;
        clone.left += Math.sin(rad) * normalOffset;
        clone.top -= Math.cos(rad) * normalOffset;
      }

      /* ---------- Group handling ---------- */
      if (clone.type === "group" && values.objByPartRotate) {
        if (values.groupMode === "Sequential") {
          clone.forEachObject((child, index) => {
            child.visible =
              i % 2 === 0
                ? index !== clone._objects.length - 1
                : index === clone._objects.length - 1;
          });
        } else if (values.groupMode === "Random") {
          const toggle = Math.random() >= 0.5;
          clone.forEachObject((child, index) => {
            child.visible = toggle
              ? index !== clone._objects.length - 1
              : index === clone._objects.length - 1;
          });
        }
      }

      /* ---------- Finalize ---------- */
      clone.class =
        values.mode === "Cloned"
          ? "distributeCopy-cloned"
          : "distributeCopy";

      clone.setCoords();
      canvas.add(clone);
      canvas.bringToFront(clone);
    }
  });

  /* ---------------------------------------------------
   * 7️⃣ Remove original pattern if mode = Moved
   * --------------------------------------------------- */
  if (values.mode === "Moved") {
    canvas.remove(patternObject);
    canvas.patternObject = null;
  }

  canvas.discardActiveObject();
  canvas.requestRenderAll();

  console.log("✔ Pattern distributed successfully");
}


/**
 * Helper function to calculate the length of a path
 */
function calculatePathLength(path, transformMatrix, activeObj) {
  let length = 0;
  let lastPoint = [0, 0]; // Default starting point
  let startPoint = [0, 0]; // Track the starting point for the "Z" command
  console.log('distribute: calculatePathLength: ', path, transformMatrix, activeObj);

  for (let i = 0; i < path.length; i++) {
    const [command, ...coords] = path[i];
    const transformed = transformPoint(coords, transformMatrix, activeObj);
    console.log('distribute: coords, trnasformedPoint: ', coords, transformed);
    if (command === "M") {
      // Move command, update start and last point
      startPoint = transformed;
      lastPoint = transformed;
    } else if (command === "L") {
      // Line segment
      if (lastPoint[0] !== undefined && lastPoint[1] !== undefined && transformed[0] !== undefined && transformed[1] !== undefined) {
        length += Math.sqrt((transformed[0] - lastPoint[0]) ** 2 + (transformed[1] - lastPoint[1]) ** 2);
      }
      lastPoint = transformed;
    } 
    else if (command === "C") {
      // Cubic Bézier curve
      const [cp1x, cp1y, cp2x, cp2y, x2, y2] = coords;
      const [x1, y1] = lastPoint; // Use the last point as the starting point

      if (
        x1 !== undefined &&
        y1 !== undefined &&
        cp1x !== undefined &&
        cp1y !== undefined &&
        cp2x !== undefined &&
        cp2y !== undefined &&
        x2 !== undefined &&
        y2 !== undefined
      ) {
        // Transform all control points and end point
        const [transCp1x, transCp1y] = transformPoint([cp1x, cp1y], transformMatrix, activeObj);
        const [transCp2x, transCp2y] = transformPoint([cp2x, cp2y], transformMatrix, activeObj);
        const [transX2, transY2] = transformPoint([x2, y2], transformMatrix, activeObj);

        // Create a Bezier curve using Bezier.js
        const curve = new Bezier(
          x1, y1,
          transCp1x, transCp1y,
          transCp2x, transCp2y,
          transX2, transY2
        );

        // Add the length of the cubic Bézier curve
        length += curve.length();
      }

      // Update last point to the end of the cubic curve
      lastPoint = transformPoint([x2, y2], transformMatrix, activeObj);
    }
    else if (command === "Z") {
      // Close the path: connect last point to the starting point
      if (lastPoint[0] !== undefined && lastPoint[1] !== undefined && startPoint[0] !== undefined && startPoint[1] !== undefined) {
        length += Math.sqrt((startPoint[0] - lastPoint[0]) ** 2 + (startPoint[1] - lastPoint[1]) ** 2);
      }
    }
  }

  return length;
}


/**
 * Transform a point using the transformation matrix.
 */
function transformPoint(coords, matrix,activeObj) {
  if (!coords || coords.length < 2) return [undefined, undefined];
  const [x, y] = coords;
  const point = new fabric.Point(x-activeObj.pathOffset.x, y-activeObj.pathOffset.y);
  const transformed = fabric.util.transformPoint(point, matrix);
  return [transformed.x, transformed.y];
}




function getPointAtLength(path, targetLength, activeObj) {
  let accumulated = 0;
  const transformMatrix = activeObj.calcTransformMatrix();
  let lastPoint = [0, 0];
  let startPoint = [0, 0];

  for (let i = 0; i < path.length; i++) {
    const [command, ...coords] = path[i];
    let transformedLastPoint;
    let transformedCoords;
    if(command !== "Z"){
      transformedLastPoint = transformPoint(lastPoint, transformMatrix, activeObj);
      transformedCoords = transformPoint(coords, transformMatrix, activeObj);  
    }
    else{
      transformedLastPoint = transformPoint(lastPoint, transformMatrix, activeObj);
      transformedCoords = startPoint;
    }


    if (command === "M") {
      // Move command: set starting point
      startPoint = transformedCoords;
      lastPoint = coords;
    } else if (command === "L" || command === "Z") {
      // Line segment
      const [x1, y1] = transformedLastPoint;
      const [x2, y2] = transformedCoords;
      const segmentLength = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);

      if (accumulated + segmentLength >= targetLength) {
        const ratio = (targetLength - accumulated) / segmentLength;
        return {
          x: x1 + ratio * (x2 - x1),
          y: y1 + ratio * (y2 - y1),
        };
      }

      accumulated += segmentLength;
      lastPoint = coords;
    } else if (command === "C") {
      // Cubic Bézier curve
      const [cp1x, cp1y, cp2x, cp2y, x2, y2] = coords;
      const [x1, y1] = transformedLastPoint;

      // Transform all control points and the endpoint
      const [transCp1x, transCp1y] = transformPoint([cp1x, cp1y], transformMatrix, activeObj);
      const [transCp2x, transCp2y] = transformPoint([cp2x, cp2y], transformMatrix, activeObj);
      const [transX2, transY2] = transformPoint([x2, y2], transformMatrix, activeObj);

      // Create a cubic Bézier curve using Bezier.js
      const curve = new Bezier(x1, y1, transCp1x, transCp1y, transCp2x, transCp2y, transX2, transY2);

      const segmentLength = curve.length();

      if (accumulated + segmentLength >= targetLength) {
        const ratio = (targetLength - accumulated) / segmentLength;

        // Use Bezier.js to find the point at the specified ratio
        const { x, y } = curve.get(ratio);
        return { x, y };
      }

      accumulated += segmentLength;
      lastPoint = coords.slice(-2); // Update last point to the end of the cubic curve
    }
  }

  // Return the last transformed point if targetLength exceeds the path length
  const lastTransformedPoint = transformPoint(lastPoint, transformMatrix, activeObj);
  return {
    x: lastTransformedPoint[0],
    y: lastTransformedPoint[1],
  };
}





/**
 * Helper function to calculate the tangent angle at a specific length along a path
 */
function getTangentAngle(path, targetLength, activeObj) {
  let accumulated = 0;
  const transformMatrix = activeObj.calcTransformMatrix();
  let lastPoint = [0, 0];
  let startPoint = [0, 0];

  for (let i = 0; i < path.length; i++) {
    const [command, ...coords] = path[i];
    let transformedLastPoint;
    let transformedCoords;
    if(command !== "Z"){
      transformedLastPoint = transformPoint(lastPoint, transformMatrix, activeObj);
      transformedCoords = transformPoint(coords, transformMatrix, activeObj);  
    }
    else{
      transformedLastPoint = transformPoint(lastPoint, transformMatrix, activeObj);
      transformedCoords = startPoint;
    }

    if (command === "M") {
      // Move command: set starting point
      startPoint = transformedCoords;
      lastPoint = coords;
    } else if (command === "L" || command === "Z") {
      // Line segment or closure line
      const [x1, y1] = transformedLastPoint;
      const [x2, y2] = transformedCoords;
      const segmentLength = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);

      if (accumulated + segmentLength >= targetLength) {
        // Compute the tangent angle of the line segment
        return (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
      }

      accumulated += segmentLength;
      lastPoint = coords;
    } else if (command === "C") {
      // Cubic Bézier curve
      const [cp1x, cp1y, cp2x, cp2y, x2, y2] = coords;
      const [x1, y1] = transformedLastPoint;

      // Transform all control points and the endpoint
      const [transCp1x, transCp1y] = transformPoint([cp1x, cp1y], transformMatrix, activeObj);
      const [transCp2x, transCp2y] = transformPoint([cp2x, cp2y], transformMatrix, activeObj);
      const [transX2, transY2] = transformPoint([x2, y2], transformMatrix, activeObj);

      // Create a cubic Bézier curve using Bezier.js
      const curve = new Bezier(x1, y1, transCp1x, transCp1y, transCp2x, transCp2y, transX2, transY2);

      const segmentLength = curve.length();

      if (accumulated + segmentLength >= targetLength) {
        const ratio = (targetLength - accumulated) / segmentLength;

        // Get the tangent vector at the specified ratio
        const tangent = curve.derivative(ratio);

        // Compute the tangent angle in degrees
        return (Math.atan2(tangent.y, tangent.x) * 180) / Math.PI;
      }

      accumulated += segmentLength;
      lastPoint = coords.slice(-2); // Update last point to the end of the cubic curve
    }
  }

  // Return 0 if targetLength exceeds the path length
  return 0;
}





  
  const renderPopoverContent = () => {
    if (selectedFeature) {
      const feature = path.find(item => item.label === selectedFeature);
      if(selectedFeature === "Pattern Along Path"){
        return (
          <Box sx={{ height: 500, width: 300 }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 1.25,
              py: 1
            }}>
              <Tooltip title="Back" arrow>
                <IconButton
                  edge="start"
                  onClick={handleBackButtonClick}
                  aria-label="Go back"
                  sx={{ borderRadius: 1 }}
                >
                  <ArrowBack />
                </IconButton>
              </Tooltip>
              <Typography variant="h6" sx={{ color: 'action.hover', fontWeight: 800 }}>
                {selectedFeature}
              </Typography>
              <Tooltip title="Close menu" arrow>
                <IconButton
                  edge="end"
                  onClick={handlePopoverClose}
                  aria-label="Close path menu"
                  sx={{ borderRadius: 1 }}
                >
                  <Clear />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', rowGap: 2.25, m: 1.25, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: 14 }}>Original copy will be:</Typography>
              <Select
                size='small'
                value={values.mode}
                onChange={handleChange}
                name='mode'
                sx={{ fontSize: 14, width: 150 }}
              >
                <MenuItem value="Moved">Moved</MenuItem>
                <MenuItem value="Duplicated">Duplicated</MenuItem>
                <MenuItem value="Cloned">Cloned</MenuItem>
              </Select>
            </Box>
              <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 14 }}>Spacing</Typography>
                <TextField
                  size='small'
                  sx={{ width: 150 }}
                  value={values.spacing}
                  onChange={handleChange}
                  name='spacing'
                  type="number"
                />
              </Box>
              <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 14 }}>Follow Path Orientation:</Typography>
                <Checkbox
                  value={values.tangentialRotate}
                  checked={values.tangentialRotate}
                  name='tangentialRotate'
                  onChange={handleChange}
                />
              </Box>
              <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 14 }}>Rotate Copies By 90°</Typography>
                <Checkbox
                  value={values.rotate}
                  checked={values.rotate}
                  name='rotate'
                  onChange={handleChange}
                />
              </Box>
              <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 14 }}>Normal Offset</Typography>
                <TextField
                  size='small'
                  sx={{ width: 150 }}
                  value={values.normalOff}
                  onChange={handleChange}
                  name='normalOff'
                  type="number"
                />
              </Box>
              <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 14 }}>Tangential Offset</Typography>
                <TextField
                  size='small'
                  sx={{ width: 150 }}
                  value={values.tangentialOff}
                  onChange={handleChange}
                  name='tangentialOff'
                  type="number"
                />
              </Box>
              <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 14 }}>If Pattern is Group, handle each Individual Object:</Typography>
                <Checkbox
                  value={values.objByPartRotate}
                  checked={values.objByPartRotate}
                  name='objByPartRotate'
                  onChange={handleChange}
                />
              </Box>

              {values.objByPartRotate && (
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 14 }}>Group Members Generation:</Typography>
                  <Select
                    size='small'
                    value={values.groupMode}
                    onChange={handleChange}
                    name='groupMode'
                    sx={{ fontSize: 14, width: 150 }}
                  >
                    <MenuItem value="Random">Random</MenuItem>
                    <MenuItem value="Sequential">Sequential</MenuItem>
                  </Select>
                </Box>
              )}
              <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                <Button
                  variant='contained'
                  onClick={handleCopyShape}
                  aria-label="Copy pattern"
                >
                  Copy
                </Button>
                <Button
                  onClick={distributeAlongPath}
                  variant='contained'
                  disabled={!copiedObject}
                  aria-label="Apply pattern along path"
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Box>
        );
      }
      if(selectedFeature === "Mirror Rotations"){
        return  <CustomRotateCopiesPopoverContent
        handlePathRotateCopies={handlePathRotateCopies}
        rotationSettings={rotationSettings}
        updateRotationSettings={updateRotationSettings}
        handlePopoverClose={handlePopoverClose}
        handleBackButtonClick={handleBackButtonClick}
        applyColorToSelectedCopy={applyColorToSelectedCopy}
        setSelectedColorForRotationCopy={setSelectedColorForRotationCopy}
        selectedColorForRotationCopy={selectedColorForRotationCopy}
        selectedStrokeForRotationCopy={selectedStrokeForRotationCopy}
        setSelectedStrokeForRotationCopy={setSelectedStrokeForRotationCopy}
        color={color}
        handleSpraying={handleSpraying}
        opacityObj={opacityObj}
        changeOpacityOfObj={changeOpacityOfObj}
      />
      }
      return (
        <Box sx={{ height: 500, width: 300 }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 1.25,
            py: 1
          }}>
            <Tooltip title="Back" arrow>
              <IconButton
                edge="start"
                onClick={handleBackButtonClick}
                aria-label="Go back"
                sx={{ borderRadius: 1 }}
              >
                <ArrowBack />
              </IconButton>
            </Tooltip>
            <Typography variant="h6" sx={{ color: 'action.hover', fontWeight: 800 }}>
              {selectedFeature}
            </Typography>
            <Tooltip title="Close menu" arrow>
              <IconButton
                edge="end"
                onClick={handlePopoverClose}
                aria-label="Close path menu"
                sx={{ borderRadius: 1 }}
              >
                <Clear />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, m: 1.25, flexWrap: 'wrap' }}>
            {feature?.children?.map((obj, index) => (
              <Tooltip key={index} title={obj.label} arrow placement="bottom">
                <PopoverListButtons
                  onClick={obj.onClick}
                  aria-label={obj.label}
                >
                  {obj.icon}
                </PopoverListButtons>
              </Tooltip>
            ))}
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ height: 500, width: 300 }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1.25,
          py: 1
        }}>
          <Typography variant="h6" sx={{ color: 'action.hover', fontWeight: 800 }}>
            Path
          </Typography>
          <Tooltip title="Close menu" arrow>
            <IconButton
              edge="end"
              onClick={handlePopoverClose}
              aria-label="Close path menu"
              sx={{ borderRadius: 1 }}
            >
              <Clear />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, m: 1.25 }}>
          {path.map((obj, index) => (
            <Button
              key={index}
              onClick={obj.onClick}
              variant='outlined'
              aria-label={obj.label}
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                display: 'flex',
                transition: (theme) => theme.transitions.create(['transform'], {
                  duration: theme.transitions.duration.shorter,
                }),
                '&:hover': {
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {obj.icon}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {obj.label}
              </Box>
            </Button>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <>
      {renderPopoverContent()}
    </>
  );
};

export default CustomPathPopoverContent;
